import { Box, Heading, Stack, Text } from '@chakra-ui/react'
import {
  faBalanceScale,
  faChalkboardUser,
  faClipboardCheck,
  faCommentSmile,
  faGlobe,
  faHandshake,
  faMapMarkerAlt,
  faPencil,
  faShieldCheck,
  faStamp,
  faThumbsUp,
  faUserClock,
  faUserCog,
  faUserHeadset,
  faUsers,
  faUsersClass,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import 'swiper/swiper-bundle.css'

import { countryCodeToName } from '@/utils/helpers'
import type { VolunteerTeamAttributes } from '@/server/models'

import UserQ from '@/queries/UserQ'
import UserVolunteerRoleQ from '@/queries/UserVolunteerRoleQ'
import type { Query } from '@/client/query'

import TextWithLines from '@/components/TextWithLines/TextWithLines'
import { userVolunteerRoleDetailsFromQuery } from '@/components/Worlds/transforms/detailsFromQueries'

import ProfileItemContainer from './ProfileItemContainer'

const MONTH_YEAR_FORMAT = 'LLL yyyy'

const VOLUNTEER_TEAM_TO_ICON: Record<VolunteerTeamAttributes['slug'], IconDefinition> = {
  blog: faPencil,
  certification: faStamp,
  coaching: faUsers,
  'community-engagement': faCommentSmile,
  'content-review': faBalanceScale,
  india: faGlobe,
  'live-help': faUserClock,
  moderators: faShieldCheck,
  'onboarding-buddy': faHandshake,
  'peer-review': faClipboardCheck,
  safety: faShieldCheck,
  'sat-guides': faChalkboardUser,
  'social-media': faThumbsUp,
  'tutoring-operations': faUserCog,
  'user-support': faUserHeadset,
  welcoming: faHandshake,
}

function AboutItem({
  title,
  icon,
  subtitle = null,
}: {
  title: string
  icon: IconDefinition
  subtitle?: string | null
}): React.ReactElement {
  return (
    <Stack direction="row" spacing={4} align="center">
      <Box minW="20px" color="fg.subtle" textAlign="center">
        <FontAwesomeIcon icon={icon} />
      </Box>
      <Box>
        <Text fontWeight={600} color="fg">
          {title}
        </Text>
        {subtitle == null ? null : (
          <Text color="fg.muted" fontSize="14px">
            {subtitle}
          </Text>
        )}
      </Box>
    </Stack>
  )
}

function VolunteerItem({
  title,
  icon,
  startDate,
  endDate,
}: {
  title: string
  icon: IconDefinition
  startDate: DateTime
  endDate: DateTime | null
}): React.ReactElement {
  const formattedStartDate = startDate.toFormat(MONTH_YEAR_FORMAT)
  const formattedEndDate = endDate == null ? null : endDate.toFormat(MONTH_YEAR_FORMAT)

  const subtitle =
    endDate == null
      ? `${formattedStartDate} - Present`
      : formattedEndDate === formattedStartDate
        ? `${formattedStartDate}`
        : `${formattedStartDate} - ${formattedEndDate}`

  return <AboutItem title={title} icon={icon} subtitle={subtitle} />
}

export default function ProfileAbout({ user }: { user: Query }): JSX.Element | null {
  const userBio = user(UserQ.bio) ?? ''
  const isUserTutor = user(UserQ.hasTutorRole()) ?? false
  const tutorDate =
    user(UserQ.getTutorSinceDate()) == null
      ? null
      : DateTime.fromISO(`${user(UserQ.getTutorSinceDate())}`)
  const userCountry = countryCodeToName(user(UserQ.country) ?? '') ?? ''
  const volunteerRoles = user
    .many(UserVolunteerRoleQ.where({ userId: UserQ.id }))
    .map(userVolunteerRoleDetailsFromQuery)

  return (
    <Box>
      <Heading size="xl" mb={6}>
        About
      </Heading>
      <ProfileItemContainer>
        {userBio.length > 0 ? (
          <>
            <TextWithLines text={userBio} maxLines={Number.POSITIVE_INFINITY} enableLinks />
            <Box borderBottom="2px solid" borderColor="border" my={6} />
          </>
        ) : null}
        <Stack direction="column" spacing={6}>
          {isUserTutor && tutorDate != null ? (
            <VolunteerItem title="Tutor" icon={faUsersClass} startDate={tutorDate} endDate={null} />
          ) : null}
          {volunteerRoles.map(({ id, teamName, teamSlug, startDate, endDate }) => (
            <VolunteerItem
              title={`${teamName} Team`}
              icon={VOLUNTEER_TEAM_TO_ICON[teamSlug] ?? faUsers}
              key={id}
              startDate={startDate}
              endDate={endDate}
            />
          ))}
          <AboutItem title={userCountry} icon={faMapMarkerAlt} />
        </Stack>
      </ProfileItemContainer>
    </Box>
  )
}
